import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ChevronRight, LogOut, Search, X } from 'lucide-react';
import korzaLogoWhite from '@/assets/korza_logo_white.png';
import { GrainOverlay } from '@/components/brand/grain-overlay';
import { LongArrow } from '@/components/brand/long-arrow';
import type { TenantDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import { StoreIcon } from '@/pages/select-tenant/store-icon';
import type { StorePickerProps } from '@/pages/select-tenant/store-picker-props';
import {
  ALPHABET,
  groupByInitial,
  matchName,
  type NameMatch,
  sortByName,
} from '@/pages/select-tenant/store-search';

const HEADER_GRADIENT =
  'radial-gradient(40% 70% at 82% 10%, rgba(21,93,252,0.9), transparent 70%), radial-gradient(35% 60% at 10% 120%, rgba(120,160,255,0.45), transparent 70%), linear-gradient(170deg, #0B348C 0%, #15191E 75%)';

interface StoreMatch {
  tenant: TenantDto;
  name: string;
  match: NameMatch;
}

/** Element id of a letter's group, the target of the A–Z jump bar. */
const groupId = (letter: string) => `stores-${letter}`;

/**
 * Store choice for Korza staff (backoffice users see every store): the focus
 * is the store name, so it's a dense alphabetical grid with search by name.
 */
export function StaffStorePicker({
  tenants,
  creationRequests,
  email,
  onSelect,
  onLogout,
}: StorePickerProps) {
  const { LL, locale } = useI18nContext();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const sorted = useMemo(
    () => sortByName(tenants, locale === 'pt' ? 'pt-BR' : locale),
    [tenants, locale]
  );
  const matches = useMemo(
    () =>
      sorted.flatMap((tenant): StoreMatch[] => {
        const match = matchName(tenant.name, query);
        return match ? [{ tenant, name: tenant.name, match }] : [];
      }),
    [sorted, query]
  );
  const groups = useMemo(() => groupByInitial(matches), [matches]);
  const lettersWithStores = new Set(groups.map((g) => g.letter));
  const hasQuery = query.trim().length > 0;

  // "/" jumps to the search, like most dense lists in dev/ops tools.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const jumpTo = (letter: string) =>
    document
      .getElementById(groupId(letter))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="min-h-dvh w-full bg-[#F9F9F9] text-[#15191E]">
      <div className="relative overflow-hidden bg-[#15191E] text-white">
        <div
          aria-hidden="true"
          className="absolute -inset-x-[10%] -inset-y-[30%]"
          style={{ background: HEADER_GRADIENT }}
        />
        <GrainOverlay />

        <header className="relative flex h-[60px] items-center gap-2.5 border-white/10 pl-5 pr-2 md:h-[72px] md:gap-3.5 md:border-b md:px-12">
          <img
            src={korzaLogoWhite}
            alt="Korza"
            className="h-3.5 w-auto md:h-[18px]"
          />
          <span className="inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full border border-white/[.22] px-2 text-[9.5px] font-semibold uppercase tracking-[0.12em] md:h-[26px] md:px-2.5 md:text-[11px]">
            <span
              aria-hidden="true"
              className="size-1.5 rounded-full bg-[#6FA0FF]"
            />
            {LL.selectTenant.staff.badge()}
            <span className="max-md:hidden">
              · {LL.selectTenant.staff.badgeAccess()}
            </span>
          </span>
          <div className="flex-1" />
          {email && (
            <span className="hidden text-sm text-white/75 md:inline">
              {email}
            </span>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="flex min-h-11 items-center gap-2 rounded-[10px] px-3 text-sm font-medium outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white"
          >
            <LogOut className="size-[18px]" strokeWidth={1.5} />
            <span className="max-md:sr-only">
              {LL.selectTenant.logoutButton()}
            </span>
          </button>
        </header>

        <div className="relative mx-auto flex max-w-[1344px] flex-col gap-4 px-5 pb-5 pt-3.5 md:gap-[22px] md:px-12 md:pb-7 md:pt-11">
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div className="flex flex-col gap-3">
              <div className="hidden items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/75 md:flex">
                <span>{LL.selectTenant.staff.eyebrow()}</span>
                <span aria-hidden="true" className="h-px w-12 bg-white/30" />
                <span>A–Z</span>
              </div>
              <h1 className="text-[28px] font-medium leading-[1.1] tracking-[-0.03em] md:text-[40px] md:leading-[1.05]">
                {LL.selectTenant.staff.title()}
              </h1>
            </div>
            <span
              className="text-xs text-white/75 md:text-sm"
              aria-live="polite"
            >
              {hasQuery
                ? LL.selectTenant.staff.resultCount({
                    shown: matches.length,
                    total: tenants.length,
                  })
                : LL.selectTenant.staff.storeCount({ count: tenants.length })}
            </span>
          </div>

          <div className="flex h-[52px] items-center gap-2.5 rounded-[14px] bg-white pl-4 pr-1 text-[#15191E] shadow-[0_20px_40px_-20px_rgba(0,0,0,0.5)] transition-shadow focus-within:ring-4 focus-within:ring-[#155DFC]/40 md:h-[60px] md:gap-3.5 md:rounded-2xl md:pl-5 md:pr-2">
            <Search
              aria-hidden="true"
              className="size-5 shrink-0 md:size-[22px]"
              strokeWidth={1.5}
            />
            <input
              ref={searchRef}
              type="search"
              aria-label={LL.selectTenant.staff.searchLabel()}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={LL.selectTenant.staff.searchPlaceholder()}
              autoComplete="off"
              className="h-full min-w-0 flex-1 bg-transparent text-base text-[#15191E] outline-none placeholder:text-[#6B7280] md:text-[17px] [&::-webkit-search-cancel-button]:hidden"
            />
            {hasQuery ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  searchRef.current?.focus();
                }}
                aria-label={LL.selectTenant.staff.clearSearch()}
                className="flex size-11 shrink-0 items-center justify-center rounded-xl text-[#5B6371] outline-none hover:bg-[#F1F2F4] focus-visible:ring-2 focus-visible:ring-[#155DFC] md:bg-[#F1F2F4] md:text-[#15191E]"
              >
                <X className="size-4" strokeWidth={1.8} />
              </button>
            ) : (
              <kbd className="mr-2 hidden h-[30px] min-w-[30px] items-center justify-center rounded-lg border border-[#E3E5E9] bg-[#F7F8F9] px-2 font-sans text-[13px] font-semibold text-[#5B6371] md:flex">
                /
              </kbd>
            )}
          </div>

          <nav
            aria-label={LL.selectTenant.staff.jumpToLetter()}
            className="hidden flex-wrap gap-0.5 md:flex"
          >
            {ALPHABET.map((letter) =>
              lettersWithStores.has(letter) ? (
                <button
                  key={letter}
                  type="button"
                  onClick={() => jumpTo(letter)}
                  className="flex size-8 items-center justify-center rounded-lg text-[13px] font-semibold text-white/85 outline-none transition-colors hover:bg-white/[.14] hover:text-white focus-visible:ring-2 focus-visible:ring-white"
                >
                  {letter}
                </button>
              ) : (
                <span
                  key={letter}
                  aria-hidden="true"
                  className="flex size-8 items-center justify-center text-[13px] font-semibold text-white/30"
                >
                  {letter}
                </span>
              )
            )}
          </nav>
        </div>
      </div>

      <main className="mx-auto flex max-w-[1344px] flex-col gap-1 pb-20 pt-1 md:gap-7 md:px-12 md:pt-7">
        {creationRequests.length > 0 && (
          <section className="flex flex-col gap-3 max-md:px-5 max-md:pt-3">
            <GroupHeading label={LL.selectTenant.staff.appsInProgress()} />
            <div className="flex flex-wrap gap-2.5">
              {creationRequests.map((request) => (
                <Link
                  key={request.slug}
                  to={`/onboarding/${request.slug}`}
                  className="group flex h-12 items-center gap-3 rounded-[14px] border-[1.5px] border-dashed border-[#155DFC]/45 px-4 text-sm font-semibold text-[#155DFC] outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-[#155DFC]"
                >
                  {request.storeName ?? LL.selectTenant.noName()}
                  <LongArrow lineClassName="w-4 group-hover:w-7" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {groups.map((group) => (
          <section
            key={group.letter}
            id={groupId(group.letter)}
            aria-label={group.letter}
            className="flex scroll-mt-4 flex-col md:gap-3"
          >
            <GroupHeading
              label={group.letter}
              count={LL.selectTenant.staff.groupCount({
                count: group.items.length,
              })}
            />
            <ul className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(232px,1fr))] md:gap-2.5">
              {group.items.map(({ tenant, match }) => (
                <li key={tenant.id} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => onSelect(tenant.slug)}
                    className="group flex h-[60px] w-full min-w-0 items-center gap-3.5 px-5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#155DFC] active:bg-[#EEF2FB] md:h-[68px] md:gap-3 md:rounded-[14px] md:border md:border-[#15191E]/[.08] md:bg-white md:pl-3 md:pr-3.5 md:transition-[border-color,box-shadow,transform] md:duration-200 md:hover:-translate-y-0.5 md:hover:border-[#155DFC]/55 md:hover:shadow-[0_12px_28px_-16px_rgba(11,52,140,0.45)] motion-reduce:transition-none"
                  >
                    <StoreIcon
                      src={tenant.iconUrl}
                      className="size-10 rounded-[10px] ring-1 ring-inset ring-[#15191E]/[.06] md:size-[42px] md:rounded-[11px]"
                    />
                    <span className="min-w-0 flex-1 truncate text-base font-semibold tracking-[-0.01em] md:text-[15px]">
                      {match.before}
                      {match.match && (
                        <mark className="rounded-[3px] bg-[#155DFC]/[.14] px-px text-[#0B348C]">
                          {match.match}
                        </mark>
                      )}
                      {match.after}
                    </span>
                    <ChevronRight
                      aria-hidden="true"
                      className="size-4 shrink-0 text-[#9AA0AA] md:hidden"
                      strokeWidth={1.5}
                    />
                    <LongArrow
                      className="hidden -translate-x-1 text-[#155DFC] opacity-0 transition-[opacity,transform] duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 md:flex"
                      lineClassName="w-3.5"
                    />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {hasQuery && matches.length === 0 && (
          <div className="flex flex-col items-center gap-2.5 px-6 py-16 text-center md:py-[72px]">
            <span className="flex size-14 items-center justify-center rounded-2xl bg-[#EEF0F3] text-[#6B7280]">
              <Search className="size-6" strokeWidth={1.5} />
            </span>
            <span className="text-lg font-semibold">
              {LL.selectTenant.staff.noResults({ query: query.trim() })}
            </span>
            <span className="text-sm text-[#5B6371]">
              {LL.selectTenant.staff.noResultsHint()}
            </span>
          </div>
        )}

        {!hasQuery && tenants.length === 0 && creationRequests.length === 0 && (
          <p className="px-5 py-8 text-[#4B5361] md:px-0">
            {LL.selectTenant.noAppsFound()}
          </p>
        )}
      </main>
    </div>
  );
}

function GroupHeading({ label, count }: { label: string; count?: string }) {
  return (
    <div className="sticky top-0 z-[1] flex h-9 items-center gap-2.5 bg-[#F9F9F9]/95 px-5 backdrop-blur-sm md:static md:h-auto md:gap-3.5 md:bg-transparent md:px-0 md:backdrop-blur-none">
      <h2 className="text-[15px] font-bold text-[#155DFC] md:text-[22px] md:font-semibold md:tracking-[-0.02em]">
        {label}
      </h2>
      <span aria-hidden="true" className="h-px flex-1 bg-[#15191E]/[.08]" />
      {count && (
        <span className="hidden text-xs font-medium text-[#6B7280] md:inline">
          {count}
        </span>
      )}
    </div>
  );
}
